'use client';

import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import React, { useState } from 'react';

//Partials
import Section from "@/components/partials/section";
import Button from "@/components/partials/button";

//Heroicons
import { ArrowLongRightIcon, ArrowRightCircleIcon } from "@heroicons/react/24/solid";

//Types
import { Config } from '@/types/config.type';
import Image from "next/image";

type LogoElement = {
    src: string;
    alt: string;
    width?: number;
    height?: number;
}

type ButtonElement = {
    text: string;
    url: string;
    type: string;
    target?: string;
}

type ContentItem = {
    buttons?: ButtonElement[];
    logos?: LogoElement[];
}

type CtaBannerBlockProps = {
    data: {
        backgroundClasses?: string;
        alignment: string;
        leading?: string;
        title: string;
        description: string;
        content: ContentItem[];
        imageSrc: string;
    }
    config: Config;
}

const CtaBannerBlock = ({
    data,
    config,
}: CtaBannerBlockProps) => {
    const [visible, setVisible] = useState(true);

    const { backgroundClasses, alignment, leading, title, description, content, imageSrc } = data;
    const buttons = content.flatMap(c => c.buttons || []);
    const logos = content.flatMap(c => c.logos || []);

    const { company, settings } = config;

    const alignmentOptions = {
        isCentered: alignment === 'center',
        isRight: alignment === 'right',
        classes: alignment === 'center'
            ? 'mx-auto text-center'
            : alignment === 'right'
                ? 'max-md:mx-auto max-md:text-center md:ml-auto md:text-right'
                : 'text-center md:text-left'
    };

    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, y: 50 },
                visible: { opacity: 1, y: 0 }
            }}
            initial="hidden"
            animate={visible ? "visible" : "hidden"}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="cta-banner bottom-0 left-0 right-0 z-50 md:mt-24 sm:bottom-6">

            <div className={`${backgroundClasses}`}>
                <Section
                    id={CtaBannerBlock.name + title}
                    className="relative overflow-hidden"
                    containerClassName="!py-16 !mt-0"
                    wrapperClassName={`flex items-center flex-col md:${alignment === "center" ? "items-center flex-col" : alignment === "right" ? "items-end md:flex-row-reverse" : "items-start md:flex-row"}`}
                    header={{
                        leading: leading,
                        classGroup: {
                            h1: "text-3xl sm:text-4xl"
                        },
                        alignment: alignment,
                        title: title,
                        description: description,
                    }}
                    action={
                        (logos.length > 0 || buttons.length > 0) && (
                            <>
                                {logos.length > 0 && (
                                    <div className="flex flex-wrap justify-center gap-2 mb-16">
                                        <h4 className={`w-full font-medium text-sm uppercase text-neutral-950 ${alignment === "center" ? "text-center" : alignment === "right" ? "text-right" : "text-left"}`}>Association and Tools We Work With</h4>
                                        <div className="flex flex-wrap justify-center gap-4">
                                            {logos.map((logo: LogoElement, index: number) => (
                                                <figure
                                                    key={index}
                                                    className="flex p-2 rounded-3xl items-center justify-center 
                 flex-none md:flex-1"
                                                >
                                                    <Image
                                                        className="h-auto object-contain w-[100px] md:w-[200px]"
                                                        width={logo.width || 100}
                                                        height={logo.height || 100}
                                                        src={logo.src}
                                                        alt={logo.alt}
                                                    />
                                                </figure>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {buttons.length > 0 && (
                                    <div className={`ffffff flex items-center flex-col gap-4 md:${alignment === "center" ? "items-center flex-col" : alignment === "right" ? "items-end md:flex-row-reverse" : "items-start md:flex-row"}`}>
                                        {buttons.map((btn: ButtonElement, index: number) => (
                                            <Button
                                                key={index}
                                                id={`${CtaBannerBlock.name}-${btn.text.replace(/\s+/g, '')}`}
                                                href={btn.url}
                                                type={btn.type || 'contained'}
                                                target={btn.target || '_self'}
                                                className="flex text-sm md:text-lg"
                                                ariaLabel={btn.text}
                                                icon={
                                                    btn.text.toLowerCase().includes("schedule") || btn.text.toLowerCase().includes("book") ? (
                                                        <ArrowRightCircleIcon className="flex-shrink-0 w-5 h-5 ms-4 -rotate-45 md:w-7 md:h-7" />
                                                    ) : btn.text.toLowerCase().includes("partner") ? (
                                                        <ArrowLongRightIcon className="flex-shrink-0 w-5 h-5 ms-4 md:w-7 md:h-7" />
                                                    ) : undefined
                                                }
                                                iconPosition="right"
                                            >
                                                {btn.text}
                                            </Button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )
                    }
                >
                    <div aria-hidden className="pointer-events-none absolute inset-0 z-[1] rounded-[inherit] [background: linear-gradient(0deg,rgba(0,0,0,.06)_1px,transparent_1px), linear-gradient(90deg,rgba(0,0,0,.06)_1px,transparent_1px) ] [background-size:100%_10px,10px_100%] " />
                    <figure className={`flex flex-1 p-2 rounded-3xl items-center justify-center ${alignment === "right" ? "md:order-first" : ""}`}>
                        <Image
                            className="h-auto object-contain w-full"
                            src={imageSrc}
                            alt={title}
                            width={500}
                            height={500}
                            sizes="(max-width: 768px) 100vw, 50vw"
                            priority
                            fetchPriority="high"
                        />
                    </figure>
                </Section>
            </div>
        </motion.div>
    );
}

export default CtaBannerBlock;
